import { useState, useEffect, useCallback, useRef } from 'react'
import { Bell } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useNotifications } from '@/hooks/useApi'
import type { ApiNotification } from '@/lib/apiTypes'
import { fmtDateShort } from '@/lib/datetime'

const LAST_SEEN_KEY = 'lastNotificationSeen'

function getLastSeen(): string | null {
  try {
    return localStorage.getItem(LAST_SEEN_KEY)
  } catch {
    return null
  }
}

function setLastSeen(iso: string) {
  try {
    localStorage.setItem(LAST_SEEN_KEY, iso)
  } catch {
    // localStorage unavailable
  }
}

function relativeTime(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffSec = Math.max(0, Math.floor((now - then) / 1000))

  if (diffSec < 60) return 'just now'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 30) return `${diffDay}d ago`
  return fmtDateShort(dateStr)
}

function contactName(n: ApiNotification): string | null {
  if (!n.contact) return null
  return `${n.contact.firstName} ${n.contact.lastName}`.trim() || null
}

function LoadingSkeleton() {
  return (
    <div className="space-y-1 p-1">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={`skeleton-${i}`} className="px-3 py-2.5 space-y-1.5 animate-pulse">
          <div className="h-3 w-3/4 bg-white/[0.06] rounded" />
          <div className="h-2.5 w-1/3 bg-white/[0.04] rounded" />
        </div>
      ))}
    </div>
  )
}

export function NotificationDropdown() {
  const [open, setOpen] = useState(false)
  const [hasUnseen, setHasUnseen] = useState(false)
  const { data, loading, refetch } = useNotifications()
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Determine if there are unseen notifications
  useEffect(() => {
    if (!data?.items?.length) {
      setHasUnseen(false)
      return
    }
    const lastSeen = getLastSeen()
    if (!lastSeen) {
      setHasUnseen(true)
      return
    }
    const newest = data.items[0]?.createdAt
    if (newest && new Date(newest).getTime() > new Date(lastSeen).getTime()) {
      setHasUnseen(true)
    } else {
      setHasUnseen(false)
    }
  }, [data])

  const handleOpen = useCallback(() => {
    setOpen(true)
    setLastSeen(new Date().toISOString())
    setHasUnseen(false)
    refetch()
  }, [refetch])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  // Click-outside to close — no full-screen backdrop so BottomTabBar stays interactive
  useEffect(() => {
    if (!open) return
    const handleOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleClose()
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open, handleClose])

  const items = data?.items ?? []

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => (open ? handleClose() : handleOpen())}
        className="relative w-10 h-10 flex items-center justify-center text-ink-300 hover:text-ink-50 hover:bg-white/[0.04] rounded-full transition-all"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {hasUnseen && (
          <span className="absolute top-2 right-2 w-2 h-2 bg-peach-200 rounded-full shadow-[0_0_6px_rgba(255,162,105,0.7)] animate-pulse-glow" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 glass-popup p-1 z-[41]">
          <div className="px-3 py-2 hairline-b mb-1">
            <p className="text-sm font-medium text-ink-100">Notifications</p>
          </div>

          {loading ? (
            <LoadingSkeleton />
          ) : items.length === 0 ? (
            <div className="px-3 py-6 text-center">
              <p className="text-sm text-white/40">No recent activity</p>
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto overscroll-contain">
              {items.map((item) => {
                const name = contactName(item)
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      handleClose()
                      if (item.contactId) navigate(`/customers/people/${item.contactId}`)
                    }}
                    className="w-full text-left px-3 py-2.5 hover:bg-white/[0.04] rounded-md transition-colors cursor-pointer"
                  >
                    <p className="text-sm text-ink-300 leading-snug line-clamp-2">
                      {item.content}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-white/40">
                        {relativeTime(item.createdAt)}
                      </span>
                      {name && (
                        <>
                          <span className="text-xs text-white/30">·</span>
                          <span className="text-xs text-white/40 truncate max-w-[120px]">
                            {name}
                          </span>
                        </>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
